import type { Theme } from '@material-ui/core/styles'
import { makeStyles, Card } from '@material-ui/core'
import type { CalendarEvent } from 'paintscout'
import AddIcon from '@material-ui/icons/Add'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import React from 'react'
import type { StyleClasses } from '@paintscout/ui'
import {
  Typography,
  Button,
  ActionButton,
  Slider,
  useClientOptions,
  Avatar,
  Spinner,
  IconButton,
  Hidden
} from '@paintscout/ui'
import moment from 'moment'
import { getCrews, getTimezone } from '@paintscout/util/builder'
import Check from '@material-ui/icons/Check'

const useStyles = makeStyles<Theme, ProductionEventDocProps>(
  (theme) => ({
    root: {
      [theme.breakpoints.down('sm')]: {
        border: 'none'
      }
    },
    actions: {
      minWidth: '110px'
    },
    titleContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      width: '100%',
      alignItems: 'center',
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      minHeight: '3.5rem',
      paddingRight: theme.spacing(0.5),
      paddingLeft: theme.spacing(2)
    },
    titleSpacer: {
      minWidth: '1px'
    },
    topDateDiv: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, minmax(110px, max-content))',
      columnGap: theme.spacing(4),
      rowGap: theme.spacing(0.5),
      justifyContent: 'flex-start',
      alignItems: 'start'
    },
    noEventBody: {
      height: '8rem',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    },
    eventBody: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2)
    },
    progressDiv: {
      paddingRight: theme.spacing(1)
    },
    dateDiv: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, minmax(110px, max-content))',
      columnGap: theme.spacing(4),
      rowGap: theme.spacing(1),
      justifyContent: 'flex-start',
      alignItems: 'start'
    },
    crewDiv: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1)
    },
    crewData: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing(1.25)
    },
    crewAvatar: {
      padding: '0px !important'
    },
    crewAvatarIcon: {
      fontSize: theme.typography.pxToRem(16),
      color: theme.palette.common.white,
      display: 'block',
      lineHeight: 1,
      WebkitFontSmoothing: 'antialiased',
      MozOsxFontSmoothing: 'grayscale',
      textRendering: 'geometricPrecision'
    },
    button: {
      marginBottom: theme.spacing(2),
      marginTop: `-${theme.spacing(1)}px`
    }
  }),
  { name: 'ProductionEventDoc' }
)

export interface ProductionEventDocProps {
  classes?: StyleClasses<typeof useStyles>
  event?: CalendarEvent
  loading?: boolean
  hideNew?: boolean
  hideTitle?: boolean
  handleCreate: () => any
  handleEdit: (event: CalendarEvent) => any
  handleCancel: (event: CalendarEvent) => any
  handleUpdate: (event: CalendarEvent) => any
}

function ProductionEventDoc(props: ProductionEventDocProps) {
  const classes = useStyles(props)
  const { options } = useClientOptions()
  const { event, loading, hideNew, hideTitle = false, handleCreate, handleEdit, handleCancel, handleUpdate } = props
  const [sliderValue, setSliderValue] = React.useState(event?.progress ?? 0)
  const crews = getCrews({ options })
  const crew = event?.crew ? crews.find((c) => c._id === event.crew) : undefined
  const projectManagerName = crew?.projectManager
    ? `${crew.projectManager.firstName} ${crew.projectManager.lastName}`
    : 'No Project Manager'
  const handleAction = (action: string) => {
    if (action === 'edit') {
      handleEdit(event)
    } else if (action === 'cancel') {
      handleCancel(event)
    }
  }
  const timezone = getTimezone({ options })

  return (
    <Card className={classes.root} data-testid={'quote-list'}>
      <div className={classes.titleContainer}>
        {event ? (
          <>
            <Hidden smDown>
              {!hideTitle ? <Typography variant="h3">Schedule</Typography> : <div className={classes.titleSpacer} />}
            </Hidden>
            <Hidden mdUp>
              <div className={classes.topDateDiv}>
                <div>
                  <Typography variant="subtitle1">Start Date</Typography>
                  <Typography variant="body1">
                    {event.start ? moment(event.start).tz(timezone).format('MMM DD, YYYY') : 'No Start Date'}
                  </Typography>
                </div>
                <div>
                  <Typography variant="subtitle1">End Date</Typography>
                  <Typography variant="body1">
                    {event.end ? moment(event.end).tz(timezone).format('MMM DD, YYYY') : 'No End Date'}
                  </Typography>
                </div>
              </div>
            </Hidden>
          </>
        ) : (
          !hideTitle && <Typography variant="h3">Schedule</Typography>
        )}
        {!event ? (
          hideNew ? null : (
            <Button
              variant="hoverUnderline"
              color="primary"
              loading={loading}
              icon={AddIcon}
              onClick={handleCreate}
              className="tour-event-table-new-button"
            >
              New
            </Button>
          )
        ) : (
          <ActionButton
            classes={{ menuItem: classes.actions }}
            data-testid="actions"
            disabled={loading}
            size="medium"
            actions={[
              {
                label: 'Edit',
                key: 'edit'
              },
              {
                key: 'divider',
                label: 'divider'
              },
              {
                label: 'Delete',
                key: 'cancel',
                scary: true
              }
            ]}
            onActionClick={(ev, action) => handleAction(action)}
            button={
              <IconButton color="inherit" disableRipple aria-label="Schedule actions" id="action-button-icon">
                <MoreVertIcon />
              </IconButton>
            }
          />
        )}
      </div>
      {loading ? (
        <div className={classes.noEventBody}>
          <Spinner />
        </div>
      ) : !event ? (
        <div className={classes.noEventBody}>
          <Typography variant="subtitle1">No event found.</Typography>
        </div>
      ) : (
        <div className={classes.eventBody}>
          <Hidden smDown>
            <div className={classes.dateDiv}>
              <div>
                <Typography variant="subtitle1">Start Date</Typography>
                <Typography variant="body1">
                  {event.start ? moment(event.start).tz(timezone).format('MMM DD, YYYY') : 'No Start Date'}
                </Typography>
              </div>
              <div>
                <Typography variant="subtitle1">End Date</Typography>
                <Typography variant="body1">
                  {event.end ? moment(event.end).tz(timezone).format('MMM DD, YYYY') : 'No End Date'}
                </Typography>
              </div>
            </div>
          </Hidden>
          <div className={classes.crewDiv}>
            <Typography variant="subtitle1">Crew</Typography>
            <div className={classes.crewData}>
              <Avatar
                className={classes.crewAvatar}
                disableTooltip
                style={{
                  width: '40px',
                  height: '40px',
                  backgroundColor: crew?.color,
                  color: '#fff',
                  transform: 'none'
                }}
              >
                <i className={`fa-light fa-users ${classes.crewAvatarIcon}`} />
              </Avatar>
              <div>
                <Typography variant="body1">{crew?.name ?? 'No Crew'}</Typography>
                <Typography variant="subtitle1">Project Manager: {projectManagerName}</Typography>
              </div>
            </div>
          </div>
          <div className={classes.progressDiv}>
            <Slider
              max={100}
              min={0}
              value={sliderValue}
              label={'Overall Progress'}
              progressLabel
              onChange={(_event: React.ChangeEvent<object>, value: number | number[]) => {
                setSliderValue(typeof value === 'number' ? value : value[0])
              }}
              onChangeCommitted={(_event: React.ChangeEvent<object>, value: number | number[]) => {
                console.log({ _event, value })
                setSliderValue(typeof value === 'number' ? value : value[0])
                handleUpdate({
                  ...event,
                  progress: typeof value === 'number' ? value : value[0],
                  previousProgress: event.progress
                })
              }}
            />
          </div>
          <Button
            variant="eventComplete"
            className={classes.button}
            active={sliderValue === 100}
            fullWidth
            startIcon={<Check />}
            onClick={() => {
              if (sliderValue === 100) {
                setSliderValue(event.previousProgress || 0)
                handleUpdate({
                  ...event,
                  progress: event.previousProgress || 0,
                  previousProgress: event.progress
                })
              } else {
                handleUpdate({
                  ...event,
                  progress: 100,
                  previousProgress: event.progress
                })
                setSliderValue(100)
              }
            }}
          >
            {sliderValue === 100 ? 'Job Complete' : 'Mark Job Complete'}
          </Button>
        </div>
      )}
    </Card>
  )
}

export default ProductionEventDoc
